import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaReact, FaVuejs, FaNodeJs, FaPython, 
  FaJava, FaDatabase, FaDocker, FaGitAlt,
  FaMobile, FaServer, FaRobot, FaBrain,
  FaCode, FaDesktop, FaAndroid, FaFilm, FaImage,
  FaTicketAlt, FaUsers, FaTasks, FaPuzzlePiece
} from 'react-icons/fa';

const Skills = () => {
  const [sectionRef, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    },
    hover: {
      y: -10,
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
      transition: { duration: 0.3, ease: 'easeOut' }
    }
  };



  const iconVariants = {
    hidden: { scale: 0, rotate: -30 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: { 
        type: 'spring',
        stiffness: 200,
        damping: 15
      }
    }
  };

  // 技能資料
  const programmingSkills = [
    { name: 'Java', icon: <FaJava className="text-orange-400" /> },
    { name: 'C', icon: <FaCode className="text-blue-500" /> },
    { name: 'C++', icon: <FaCode className="text-blue-600" /> },
    { name: 'C#', icon: <FaCode className="text-green-500" /> },
    { name: 'Python', icon: <FaPython className="text-yellow-400" /> },
    { name: 'HTML/CSS', icon: <FaCode className="text-red-400" /> },
    { name: 'JavaScript', icon: <FaCode className="text-yellow-300" /> },
    { name: 'Django/Flask', icon: <FaPython className="text-green-300" /> },
    { name: 'MySQL', icon: <FaDatabase className="text-blue-400" /> }
  ];

  const frontendSkills = [
    { name: 'React', icon: <FaReact className="text-blue-400" /> },
    { name: 'Vue.js', icon: <FaVuejs className="text-green-400" /> },
    { name: 'Web開發', icon: <FaDesktop className="text-cyan-300" /> }
  ];

  const backendSkills = [
    { name: 'Node.js', icon: <FaNodeJs className="text-green-500" /> },
    { name: 'NFT門票系統', icon: <FaTicketAlt className="text-purple-400" /> },
    { name: 'Flask/React', icon: <FaReact className="text-blue-300" /> }
  ];

  const mobileSkills = [
    { name: 'Android', icon: <FaAndroid className="text-green-500" /> },
    { name: 'iOS APP開發', icon: <FaMobile className="text-gray-400" /> },
    { name: 'Arduino', icon: <FaServer className="text-red-400" /> }
  ];

  const aiSkills = [
    { name: 'Deep Learning', icon: <FaBrain className="text-purple-400" /> },
    { name: 'Machine Learning', icon: <FaRobot className="text-indigo-400" /> },
    { name: 'SVM模型', icon: <FaBrain className="text-blue-400" /> },
    { name: 'Decision tree', icon: <FaCode className="text-green-500" /> }
  ];

  const otherSkills = [
    { name: '影像辨識', icon: <FaFilm className="text-red-500" /> },
    { name: '專題協作', icon: <FaUsers className="text-blue-400" /> },
    { name: '項目規劃', icon: <FaTasks className="text-green-500" /> },
    { name: '問題解決導向', icon: <FaPuzzlePiece className="text-yellow-500" /> }
  ];

  // 渲染技能項目
  const renderSkillItem = (skill) => (
    <motion.div 
      key={skill.name} 
      variants={cardVariants}
      whileHover="hover"
      className="flex items-center gap-3 mb-3 p-2 rounded-lg hover:bg-slate-800/50"
    >
      <motion.div 
        variants={iconVariants}
        className="w-10 h-10 flex items-center justify-center bg-slate-800/80 rounded-full"
      >
        {skill.icon}
      </motion.div>
      <span className="font-medium">{skill.name}</span>
    </motion.div>
  );

  // 技能類別卡片
  const SkillCategory = ({ title, skills, gridClass }) => (
    <motion.div 
      variants={cardVariants}
      className={`card ${gridClass}`}
    >
      <h3 className="text-xl font-semibold mb-6 text-primary-300">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {skills.map(renderSkillItem)}
      </div>
    </motion.div>
  );

  return (
    <section id="skills" className="py-20 bg-gradient-to-b from-slate-900 to-slate-800 relative">
      {/* 背景裝飾 */}
      <div className="absolute inset-0 z-0">
        <div className="absolute bottom-0 right-0 w-3/4 h-3/4 bg-gradient-radial from-primary-900/20 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 left-1/4 w-1/3 h-1/3 bg-gradient-radial from-secondary-900/20 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={sectionRef}
          variants={sectionVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.div className="text-center mb-16" variants={titleVariants}>
            <motion.h2 
              variants={titleVariants} 
              className="text-3xl md:text-4xl font-bold mb-4 inline-block"
            >
              我的<span className="text-gradient">技能</span>
            </motion.h2>
            <motion.div variants={titleVariants} className="w-20 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 mx-auto mb-8"></motion.div>
            <motion.p variants={titleVariants} className="text-lg text-gray-300 max-w-2xl mx-auto">
              作為一名全方位的資訊工程師，我擁有廣泛的技術知識和實踐經驗，
              能夠從前端到後端、從行動應用到AI應用，靈活運用各種技術完成專案需求。
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SkillCategory 
              title="程式語言" 
              skills={programmingSkills} 
              gridClass="md:col-span-2"
            />
            
            <SkillCategory 
              title="前端開發" 
              skills={frontendSkills} 
              gridClass="md:col-span-1"
            />
            
            <SkillCategory 
              title="後端開發" 
              skills={backendSkills} 
              gridClass="md:col-span-1"
            />
            
            <SkillCategory 
              title="人工智慧與機器學習" 
              skills={aiSkills} 
              gridClass="md:col-span-1"
            />
            
            <SkillCategory 
              title="行動應用開發" 
              skills={mobileSkills} 
              gridClass="md:col-span-1"
            />
            
            <SkillCategory 
              title="其他技能" 
              skills={otherSkills} 
              gridClass="md:col-span-2"
            />
          </div>

          {/* 簡短總結 */}
          <motion.div 
            className="mt-16 flex justify-center" 
            variants={cardVariants}
          >
            <motion.div 
              className="relative glassmorphism rounded-xl p-8 max-w-3xl text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.5 }}
            >
              <h3 className="text-xl font-semibold mb-4 text-center text-primary-300">個人特長</h3>
              <p className="text-gray-300">
                我擅長於多種程式語言和技術框架，涵蓋了網頁前後端開發、行動應用以及人工智慧領域。除了技術能力外，我也重視專案協作能力和解決問題的思維模式。
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
